import { TicketProps } from '../interfaces/ticketproperties.interface';
import { cpFilterParam } from '../../tikit/boardWindowing';

// The pickers in app state are loaded without archived values so no dropdown offers one, but a ticket can still
// carry an archived status, category, type or priority. Names and board lanes are resolved from both. #28462
export const ARCHIVED_PICKERS_URL_PARAMS = '?$filter=IsDeleted%20eq%20false%20and%20IsArchived%20eq%20true';

export const withArchived = (active?: TicketProps[], archived?: TicketProps[]): TicketProps[] =>
  (archived?.length ?? 0) > 0 ? [...(active ?? []), ...archived] : (active ?? []);

const byPosition = (a: TicketProps, b: TicketProps) => (a.Position ?? 0) - (b.Position ?? 0);

const laneKey = (prefix: string, id: number) => `${prefix}_${id}`;

export const archivedLaneKeys = (prefix: string, archived?: TicketProps[]): string[] =>
  (archived ?? []).map(p => laneKey(prefix, p.Id));

export interface ArchivedPickerState {
  archivedTicketStatus?: TicketProps[];
  archivedCategories?: TicketProps[];
  archivedPriority?: TicketProps[];
}

interface ArchivedLane {
  field: string;
  prefix: string;
  pickers: keyof ArchivedPickerState;
}

// The board groupings that have archived values, by boardFilter: 0 status, 1 category, 2 priority. Groups (3) and
// teams (4) have none. A Map, so a boardFilter that is none of these - undefined, a string from storage, a grouping
// added later - finds nothing rather than whatever an object index or a default would give, and every function
// below does nothing for it.
const ARCHIVED_LANES = new Map<number, ArchivedLane>([
  [0, { field: 'StatusId', prefix: 'status', pickers: 'archivedTicketStatus' }],
  [1, { field: 'CategoryId', prefix: 'category', pickers: 'archivedCategories' }],
  [2, { field: 'PriorityId', prefix: 'priority', pickers: 'archivedPriority' }],
]);

export const archivedPickersForBoard = (boardFilter: number, state: ArchivedPickerState): TicketProps[] => {
  const lane = ARCHIVED_LANES.get(boardFilter);
  return lane ? state[lane.pickers] ?? [] : [];
};

// Which archived values hold a ticket in the view, as one grouped request.
export const archivedLaneProbe = (field: string, archived: TicketProps[], viewPredicate?: string): string => {
  const inArchived = `${field} in (${archived.map(p => p.Id).join(',')})`;
  const filter = viewPredicate ? `(${inArchived}) and (${viewPredicate})` : inArchived;
  return `?$apply=filter(${filter})/groupby((${field}))`;
};

export const occupiedLaneKeys = (prefix: string, field: string, groups: Record<string, number | null>[]): string[] =>
  groups.map(g => g[field]).filter((id): id is number => id != null).map(id => laneKey(prefix, id));

export type FetchLaneGroups = (query: string) => Promise<Record<string, number | null>[]>;

// An archived value's lane is fetched only when the view has tickets in it: one grouped request finds which,
// rather than a full lane request per archived value. If that request fails, every archived lane is fetched.
export const findArchivedLaneKeys = async (
  boardFilter: number,
  state: ArchivedPickerState,
  viewPredicate: string,
  cpFilter: string,
  fetchGroups: FetchLaneGroups
): Promise<string[]> => {
  const lane = ARCHIVED_LANES.get(boardFilter);
  const archived = archivedPickersForBoard(boardFilter, state);
  if (!lane || archived.length === 0) return [];
  const { field, prefix } = lane;
  try {
    const groups = await fetchGroups(`${archivedLaneProbe(field, archived, viewPredicate)}${cpFilterParam(cpFilter)}`);
    return occupiedLaneKeys(prefix, field, groups ?? []);
  } catch (err) {
    console.warn('Board: archived lane probe failed, fetching every archived lane', err);
    return archivedLaneKeys(prefix, archived);
  }
};

// An archived value gets a lane only while a ticket is in it, after the active lanes, in its own order.
export const occupiedArchivedLanes = (
  prefix: string,
  archived: TicketProps[] | undefined,
  occupied: Set<string>
): TicketProps[] =>
  [...(archived ?? [])].sort(byPosition).filter(p => occupied.has(laneKey(prefix, p.Id)));

export const archivedBoardLanes = (
  boardFilter: number,
  state: ArchivedPickerState,
  occupied: Set<string>,
  archivedLabel: string
): Record<string, { title: string }> => {
  const lanes: Record<string, { title: string }> = {};
  const lane = ARCHIVED_LANES.get(boardFilter);
  if (!lane) return lanes;
  occupiedArchivedLanes(lane.prefix, archivedPickersForBoard(boardFilter, state), occupied).forEach(p => {
    lanes[laneKey(lane.prefix, p.Id)] = { title: `${p.Value} ${archivedLabel}` };
  });
  return lanes;
};

export const isArchivedLane = (laneKeyToCheck: string, boardFilter: number, state: ArchivedPickerState): boolean => {
  const lane = ARCHIVED_LANES.get(boardFilter);
  return !!lane && archivedPickersForBoard(boardFilter, state).some(p => laneKey(lane.prefix, p.Id) === laneKeyToCheck);
};
